import { useCallback, useEffect, useState } from 'react';
import type { Role, UserItem, UserStatus } from '@mvcp/contracts';
import { listCompanyPeople } from '../api';
import { Skeleton } from './Skeleton';

import { Avatar } from './Avatar';
const ROLE_LABEL: Record<Role, string> = {
  system_administrator: 'Administrator',
  compliance_manager: 'Manager',
  vendor: 'Vendor',
};

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  deactivated: 'Deactivated',
};

const STATUS_TONE: Record<UserStatus, string> = {
  active: 'ok',
  invited: 'warn',
  deactivated: 'bad',
};

/** The date portion of an ISO timestamp, or "Never" when the user has never signed in. */
function lastActive(iso?: string): string {
  if (!iso) return 'Never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: UserItem[] }
  | { kind: 'error'; message: string };

/**
 * Compliance Manager → People (Story 6.5, AC5). A READ-ONLY roster of the users who can
 * access the Manager's own Company — email, role, status. No management controls are
 * rendered or reachable (deactivate/invite remain System-Administrator-only). Tenant
 * scoping is enforced server-side by RLS (`GET /company/users` passes the real principal),
 * so a Manager never sees another Company's users. Mirrors the golden ViewState/load
 * template.
 */
export function PeopleView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const items = await listCompanyPeople();
      setState({ kind: 'ready', items });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>People</h1>
        <p className="muted" style={{ margin: 0 }}>
          The people who can access your company's compliance data. This list is read-only —
          contact your platform administrator to add or remove access.
        </p>
      </div>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading people">
          <Skeleton height={20} width="40%" />
          <Skeleton height={48} />
          <Skeleton height={48} />
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load people</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && (
        <section className="card stack" aria-labelledby="people-heading">
          <h2 id="people-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Company users
          </h2>

          {state.items.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No users yet.</p>
          ) : (
            /* Shared roster shape — `.table-scroll` / `.data-table` in global.css. This list
               is read-only (no actions column), but it carries the same mono email and date
               cells, and so the same mid-token-wrap risk. */
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {state.items.map((u) => (
                    <tr key={`${u.id}:${u.status}`} data-person-row={u.email}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <Avatar id={u.id} name={u.email} />
                          <span data-numeric>{u.email}</span>
                        </span>
                      </td>
                      <td>{ROLE_LABEL[u.role]}</td>
                      <td>
                        <span className="status-pill" data-tone={STATUS_TONE[u.status]}>
                          <span className="status-pill__dot" aria-hidden="true" />
                          {STATUS_LABEL[u.status]}
                        </span>
                      </td>
                      <td data-numeric data-last-active={u.email}>
                        {lastActive(u.lastLoginAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </>
  );
}
